import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd'
import { useDropzone } from 'react-dropzone'
import { ImageUploads } from '../../lib/useImageUploads'
import { formatSize, IMAGE_TYPES, LIMITS_HINT } from '../../lib/uploads'
import { Icon } from '../Icon'
import { Alert } from '../states'

interface Props {
  uploads: ImageUploads
  disabled?: boolean
  /** Comic forms choose a thumbnail; the About page doesn't */
  thumbnail?: { id: string | undefined; set: (id: string) => void }
  error?: string
  idPrefix: string
}

export function ImageList({ uploads, disabled, thumbnail, error, idPrefix }: Props) {
  const { items } = uploads
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    // Files the picker filter turns away still go through our checks, so each gets a reason
    onDrop: (accepted, rejections) => uploads.add([...accepted, ...rejections.map((r) => r.file)]),
    accept: Object.fromEntries(IMAGE_TYPES.map((t) => [t, []])),
    noClick: true,
    noKeyboard: true,
    disabled,
  })

  const onDragEnd = (r: DropResult) => {
    if (r.destination) uploads.move(r.source.index, r.destination.index)
  }

  const counts = {
    done: items.filter((i) => i.status === 'done').length,
    uploading: items.filter((i) => i.status === 'uploading').length,
    failed: items.filter((i) => i.status === 'failed').length,
    waiting: items.filter((i) => i.status === 'waiting').length,
  }
  const summary =
    uploads.busy || counts.failed
      ? [`${items.length} images.`, counts.done && `${counts.done} uploaded`, counts.uploading && `${counts.uploading} uploading`, counts.failed && `${counts.failed} failed`, counts.waiting && `${counts.waiting} waiting`]
          .filter(Boolean)
          .join(', ')
          .replace('., ', '. ') + '.'
      : `${items.length === 1 ? '1 image' : `${items.length} images`}. Drag to reorder, or use the arrow buttons.`

  return (
    <>
      {uploads.rejected.map((r) => (
        <Alert key={r.name} title={`${r.name} wasn't added`}>
          {r.reason}
        </Alert>
      ))}
      <div {...getRootProps({ className: isDragActive ? 'dz over' : 'dz' })}>
        <input {...getInputProps({ id: `${idPrefix}-files` })} />
        <span className="text-primary">
          <Icon name="upload" size={36} strokeWidth={2} />
        </span>
        <div className="h5">Drag and drop images here</div>
        <div className="hint">or</div>
        <button type="button" className="btn solid" onClick={open} disabled={disabled}>
          Choose files
        </button>
        <div className="hint">{LIMITS_HINT}</div>
      </div>
      {error && (
        <div className="ferr" role="alert">
          <Icon name="alert" size={16} />
          {error}
        </div>
      )}
      {items.length > 0 && (
        <>
          <div aria-live="polite" className="hint strong">
            {summary}
          </div>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={`${idPrefix}-list`} isDropDisabled={disabled}>
              {(drop) => (
                <ol className="list" ref={drop.innerRef} {...drop.droppableProps}>
                  {items.map((it, i) => (
                    <Draggable key={it.id} draggableId={it.id} index={i} isDragDisabled={disabled}>
                      {(drag, snap) => (
                        <li ref={drag.innerRef} {...drag.draggableProps} className={`row${it.status === 'failed' ? ' err' : ''}${snap.isDragging ? ' dragging' : ''}`}>
                          <span className="grip" {...drag.dragHandleProps} aria-label={`Drag image ${i + 1} to reorder`}>
                            <Icon name="grip" size={22} />
                          </span>
                          <div className="rowthumb">
                            {it.status === 'failed' ? (
                              <span className="text-danger">
                                <Icon name="alert" size={24} />
                              </span>
                            ) : (
                              <img src={it.preview} alt="" />
                            )}
                          </div>
                          <div className="grow vstack gap-sm">
                            {it.status === 'failed' ? (
                              <>
                                <div className="ferr" role="alert">
                                  {it.name} · Upload failed. {it.error}
                                </div>
                                <div className="hint">The file is still ready to send.</div>
                              </>
                            ) : it.status !== 'done' ? (
                              <>
                                <div className="hint strong text-ink">
                                  {it.name} · {it.status === 'waiting' ? 'Waiting to upload' : `Uploading, ${it.progress}%`}
                                </div>
                                <div className="track" role="progressbar" aria-label={`Upload progress for image ${i + 1}`} aria-valuenow={it.progress} aria-valuemin={0} aria-valuemax={100}>
                                  <div className="fill" style={{ width: `${it.progress}%` }} />
                                </div>
                              </>
                            ) : (
                              <>
                                {it.file && (
                                  <div className="hint strong text-primary-dark hstack gap-sm">
                                    <Icon name="check" size={16} />
                                    {it.name} · {formatSize(it.size ?? 0)} · Uploaded
                                  </div>
                                )}
                                <label htmlFor={`${idPrefix}-alt-${it.id}`} className="lbl sm">
                                  Alt text for image {i + 1} <span className="hint">(optional)</span>
                                </label>
                                <input
                                  id={`${idPrefix}-alt-${it.id}`}
                                  className="field compact"
                                  placeholder="Describe this image"
                                  value={it.altText}
                                  disabled={disabled}
                                  onChange={(e) => uploads.setAlt(it.id, e.target.value)}
                                />
                                {thumbnail && (
                                  <label className="thumbopt">
                                    <input type="radio" name={`${idPrefix}-thumb`} checked={thumbnail.id === it.id} disabled={disabled} onChange={() => thumbnail.set(it.id)} />
                                    Use as thumbnail
                                  </label>
                                )}
                              </>
                            )}
                          </div>
                          <div className="hstack gap-sm">
                            {it.status === 'failed' && (
                              <button type="button" className="btn sm" onClick={() => uploads.retry(it.id)}>
                                <Icon name="retry" size={18} />
                                Retry
                              </button>
                            )}
                            {it.status === 'done' && (
                              <>
                                <button type="button" className="iconbtn" aria-label={`Move image ${i + 1} earlier`} disabled={disabled || i === 0} onClick={() => uploads.move(i, i - 1)}>
                                  <Icon name="up" />
                                </button>
                                <button type="button" className="iconbtn" aria-label={`Move image ${i + 1} later`} disabled={disabled || i === items.length - 1} onClick={() => uploads.move(i, i + 1)}>
                                  <Icon name="down" />
                                </button>
                              </>
                            )}
                            <button
                              type="button"
                              className="iconbtn"
                              aria-label={it.status === 'waiting' || it.status === 'uploading' ? `Cancel upload of image ${i + 1}` : `Remove image ${i + 1}`}
                              disabled={disabled}
                              onClick={() => uploads.remove(it.id)}
                            >
                              <Icon name={it.status === 'waiting' || it.status === 'uploading' ? 'close' : 'bin'} />
                            </button>
                          </div>
                        </li>
                      )}
                    </Draggable>
                  ))}
                  {drop.placeholder}
                </ol>
              )}
            </Droppable>
          </DragDropContext>
        </>
      )}
    </>
  )
}
